import { formatTimeSpan } from "../../common/StringUtils";
import {
  FixedIntervalAggregationDto,
  HistoryQueryFixedIntervalDto,
  HistoryQueryLttbDto,
  TagSelectionAllDevicesDto,
  TagSelectionDto,
  TagSelectionScalarDto,
  TagSelectionSpecificDevicesDto,
} from "./TagQueryModels";

export function createScalarTagSelection(logicalTagName: string): TagSelectionScalarDto {
  assertLogicalTagName(logicalTagName);

  return { type: "scalar", logicalTagName };
}

export function createAllDevicesTagSelection(logicalTagName: string): TagSelectionAllDevicesDto {
  assertLogicalTagName(logicalTagName);

  return { type: "allDevices", logicalTagName };
}

export function createSpecificDevicesTagSelection(
  logicalTagName: string,
  deviceIds: readonly number[]
): TagSelectionSpecificDevicesDto {
  assertLogicalTagName(logicalTagName);

  if (deviceIds.length === 0) {
    throw new Error("A specific-device tag selection requires at least one device ID.");
  }

  const uniqueDeviceIds = new Set<number>();
  for (const deviceId of deviceIds) {
    if (!Number.isInteger(deviceId) || deviceId < -32_768 || deviceId > 32_767) {
      throw new Error(`Device ID ${deviceId} is outside the signed 16-bit range.`);
    }
    if (uniqueDeviceIds.has(deviceId)) {
      throw new Error(`Device ID ${deviceId} is duplicated in the tag selection.`);
    }
    uniqueDeviceIds.add(deviceId);
  }

  return { type: "specificDevices", logicalTagName, deviceIds: [...deviceIds] };
}

/**
 * Converts tag identities whose caller has already classified as scalar or explicitly addressed.
 * A numeric suffix is an explicit device ID, including reserved IDs such as 0 and 32767.
 * All-device producers must use createAllDevicesTagSelection instead.
 */
export function createScalarAndSpecificTagSelections(tagNames: readonly string[]): TagSelectionDto[] {
  const selections: Array<TagSelectionScalarDto | { logicalTagName: string; deviceIds: number[] }> = [];
  const specificSelectionIndexes = new Map<string, number>();

  for (const tagName of tagNames) {
    const explicitIdentity = parseExplicitDeviceTagName(tagName);
    if (!explicitIdentity) {
      selections.push(createScalarTagSelection(tagName));
      continue;
    }

    const existingIndex = specificSelectionIndexes.get(explicitIdentity.logicalTagName);
    if (existingIndex === undefined) {
      specificSelectionIndexes.set(explicitIdentity.logicalTagName, selections.length);
      selections.push({
        logicalTagName: explicitIdentity.logicalTagName,
        deviceIds: [explicitIdentity.deviceId],
      });
      continue;
    }

    const existingSelection = selections[existingIndex];
    if ("deviceIds" in existingSelection) {
      existingSelection.deviceIds.push(explicitIdentity.deviceId);
    }
  }

  return selections.map((selection) =>
    "deviceIds" in selection
      ? createSpecificDevicesTagSelection(selection.logicalTagName, selection.deviceIds)
      : selection
  );
}

export function parseExplicitDeviceTagName(
  tagName: string
): { logicalTagName: string; deviceId: number } | undefined {
  const match = tagName.match(/^(.*):(-?\d+)$/);
  if (!match) {
    return undefined;
  }

  const deviceId = Number(match[2]);
  if (!Number.isInteger(deviceId) || deviceId < -32_768 || deviceId > 32_767) {
    throw new Error(`Device ID ${match[2]} is outside the signed 16-bit range.`);
  }

  assertLogicalTagName(match[1]);
  return { logicalTagName: match[1], deviceId };
}

export function createFixedIntervalHistoryQuery(
  from: number,
  to: number,
  intervalSeconds: number,
  aggregation: FixedIntervalAggregationDto
): HistoryQueryFixedIntervalDto {
  assertHistoryRange(from, to);

  const intervalMilliseconds = intervalSeconds * 1_000;
  if (!Number.isInteger(intervalMilliseconds) || intervalMilliseconds <= 0) {
    throw new Error("A fixed history interval must be a positive whole number of milliseconds.");
  }

  return {
    type: "fixedInterval",
    from,
    to,
    interval: formatTimeSpan(intervalMilliseconds),
    aggregation,
  };
}

export function createLttbHistoryQuery(
  from: number,
  to: number,
  pointLimit: number
): HistoryQueryLttbDto {
  assertHistoryRange(from, to);

  if (!Number.isInteger(pointLimit) || pointLimit <= 0 || pointLimit > 4_294_967_295) {
    throw new Error("An LTTB point limit must be a positive unsigned 32-bit integer.");
  }

  return { type: "lttb", from, to, pointLimit };
}

function assertLogicalTagName(logicalTagName: string): void {
  if (logicalTagName.trim().length === 0) {
    throw new Error("A logical tag name is required.");
  }
}

function assertHistoryRange(from: number, to: number): void {
  if (!Number.isFinite(from) || !Number.isFinite(to) || from >= to) {
    throw new Error("A history range requires finite timestamps with from before to.");
  }
}
